import type { routeMachine } from '@polaris/core/machines'
import type { BalancesByChain } from '@polaris/core/models'
import { deleteBalancesCache } from '@polaris/core/services'
import {
  debug,
  formatPrice,
  getDeepError,
  MetaMaskCircuitBreakerError,
  UserRejectedRequestError,
} from '@polaris/core/utils'
import { toast } from 'sonner'
import type { ActorRefFromLogic } from 'xstate'
import {
  tradeFailureToast,
  tradePausedToast,
  tradeRecoveringToast,
  tradeSuccessToast,
} from '@/src/components/ui/toast'

const FAILURE_REASONS: Record<
  | 'step'
  | 'hotkeyInitialization'
  | 'startMpcRoute'
  | 'mpcInitialization'
  | 'gasFunding'
  | 'hotkeyFundTransfer'
  | 'nextStepDetermination'
  | 'destinationFundTransfer',
  string
> = {
  step: 'Failed to execute current step, please try again.',
  hotkeyInitialization: 'Failed to inizialize hotkeys',
  mpcInitialization: 'Failed to inizialize mpc',
  startMpcRoute: 'Failed to start mpc route',
  gasFunding: 'Failed to fund gas, please try again.',
  hotkeyFundTransfer: 'Failed to transfer funds to hotkey, please try again.',
  nextStepDetermination: 'Failed to retrieve next step, please try again.',
  destinationFundTransfer:
    'Failed to transfer funds to your wallet, please try again.',
}

const _calledPausedToastsIDs = new Set<string>()

type RouteMachine = ActorRefFromLogic<typeof routeMachine>

const getShouldRenderToast = () => {
  return (
    window.location.pathname !== '/' &&
    !window.location.pathname.includes('/route')
  )
}

export function onErrorInvalidate<TMachine extends RouteMachine>(
  routeMachine: TMachine,
  balancesByChains: BalancesByChain[],
) {
  routeMachine.on('routeFailed', () => {
    try {
      const addressChainIdSet = []
      for (const [_, balanceByChain] of Object.entries(balancesByChains)) {
        for (const balance of balanceByChain.balances) {
          addressChainIdSet.push({
            chain_id: balance.chain_id,
            address: balance.address,
          })
        }
      }
      deleteBalancesCache({
        addressChainIdSet,
      })
    } catch (error) {
      debug(error)
    }
  })

  return routeMachine
}

export function onErrorToasts<TMachine extends RouteMachine>(
  routeMachine: TMachine,
  isCurrentStepMode = false,
) {
  routeMachine.on('routeComplete', () => {
    const route = routeMachine.getSnapshot().context.route
    const shouldRenderToast = getShouldRenderToast()

    if (route && !route.isSuccessful && shouldRenderToast) {
      tradeSuccessToast(
        {
          from: route.InputAssetMetadata.ticker,
          to: route.OutputAssetMetadata.ticker,
        },
        route.ID,
        formatPrice(route.OutputAssetUSDPrice * route.AmountOut),
        route.destinationChain.chain_name,
        ['route'],
      )

      if (_calledPausedToastsIDs.has(route.ID)) {
        toast.dismiss(`paused-${route.ID}`)
        _calledPausedToastsIDs.delete(route.ID)
      }

      if (
        route.sourceChainEcosystemAddress &&
        route.destinationChainEcosystemAddress
      ) {
        const addressChainIdSet = [
          {
            chain_id: route.sourceChain.chain_id,
            address: route.sourceChainEcosystemAddress,
          },
          {
            chain_id: route.destinationChain.chain_id,
            address: route.destinationChainEcosystemAddress,
          },
        ]

        // Check if route involves Namada (chain_id: 'namada')
        const isNamadaRoute =
          route.sourceChain.chain_id === 'namada' ||
          route.destinationChain.chain_id === 'namada'

        // For Namada routes, add a delay to allow privacy-service (zoro) to complete wallet sync
        // This prevents caching stale balances before the sync completes
        if (isNamadaRoute) {
          setTimeout(() => {
            deleteBalancesCache({ addressChainIdSet })
          }, 2000)
        } else {
          deleteBalancesCache({ addressChainIdSet })
        }
      }
    }
  })

  routeMachine.on('routeFailed', ({ for: reason }) => {
    const route = routeMachine.getSnapshot().context.route
    const shouldRenderToast = getShouldRenderToast()

    if (route && shouldRenderToast) {
      tradeFailureToast(
        {
          from: route.InputAssetMetadata.ticker,
          to: route.OutputAssetMetadata.ticker,
        },
        route.ID,
        FAILURE_REASONS[reason],
        ['route'],
      )
    }
  })

  routeMachine.on('retryableError', ({ error }) => {
    const route = routeMachine.getSnapshot().context.route
    const shouldRenderToast = getShouldRenderToast()
    const deepError = getDeepError(error)

    if (
      !route ||
      !shouldRenderToast ||
      !isCurrentStepMode ||
      deepError instanceof UserRejectedRequestError
    )
      return

    // Provide specific messaging for MetaMask circuit breaker errors
    let errorMessage = 'Trade failed · Action required'
    if (deepError instanceof MetaMaskCircuitBreakerError) {
      errorMessage =
        'MetaMask temporarily unavailable · Retrying automatically...'
    }

    tradeFailureToast(
      {
        from: route.InputAssetMetadata.ticker,
        to: route.OutputAssetMetadata.ticker,
      },
      route.ID,
      errorMessage,
      ['route'],
    )
  })

  routeMachine.on('recoveringAutopilotFundsStarted', () => {
    const snapshot = routeMachine.getSnapshot()
    const route = snapshot.context.route
    const refundInfo = snapshot.context.refundInfo
    const shouldRenderToast = getShouldRenderToast()

    const recoveryTicker = refundInfo?.ticker

    if (route && recoveryTicker && shouldRenderToast) {
      tradeRecoveringToast(
        {
          from: route.InputAssetMetadata.ticker,
          to: route.OutputAssetMetadata.ticker,
        },
        route.ID,
        recoveryTicker,
        route.OutputAssetMetadata.logo_img_url,
        route.OutputAssetMetadata.ticker,
        ['route'],
      )
    }
  })

  routeMachine.on('recoveringAutopilotFundsComplete', () => {
    const route = routeMachine.getSnapshot().context.route

    if (route) {
      toast.dismiss(`recovering-${route.ID}`)
    }
  })

  routeMachine.subscribe(({ context, value }) => {
    const route = context.route
    const confirmed = context.confirmed
    const currentStep = context.currentStep
    const shouldRenderToast = getShouldRenderToast()

    if (
      route &&
      currentStep - 1 > route.firstStepNumber &&
      !_calledPausedToastsIDs.has(route.ID) &&
      !confirmed &&
      value === 'Idle' &&
      shouldRenderToast
    ) {
      tradePausedToast(
        {
          from: route.InputAssetMetadata.ticker,
          to: route.OutputAssetMetadata.ticker,
        },
        'Paused',
        route.ID,
        ['route'],
      )

      return _calledPausedToastsIDs.add(route.ID)
    }

    if (route && _calledPausedToastsIDs.has(route.ID) && confirmed) {
      toast.dismiss(`paused-${route.ID}`)
      return _calledPausedToastsIDs.delete(route.ID)
    }
  })

  return routeMachine
}
